import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { DISCIPLINE_LABEL, formatRelative } from "@/lib/utils";
import HealthScore from "@/components/HealthScore";
import HealthDetailsModal from "@/components/HealthDetailsModal";
import SheetThumb from "@/components/SheetThumb";
import InternalBadge from "@/components/InternalBadge";
import NewProjectModal from "@/components/admin/NewProjectModal";
import UploadSheetsModal from "@/components/admin/UploadSheetsModal";
import { canAdminPlans } from "@/lib/permissions";
import { filterPortfolioProjects } from "@/lib/portfolioFilter";
import { ResumeCard } from "@/components/ResumeCard";
import { MapPin, Briefcase, Search, FileText, ChevronRight, Layers, Flame, ShieldAlert, Plus, Upload, Settings2, Compass, Users as UsersIcon, Sparkles, BellRing, Check, Archive, RotateCcw, ChevronDown } from "lucide-react";

/**
 * Portfolio = "Plans Launchpad". Sheet-first, not dashboard-first.
 * Lead surface: recent sheets, latest revision, click-through to plans.
 * KPIs are demoted to inline counters; health is a small badge.
 */
export default function Portfolio() {
  const [projects, setProjects] = useState(null);
  const [q, setQ] = useState("");
  const [healthFor, setHealthFor] = useState(null);
  const [newProjectOpen, setNewProjectOpen] = useState(false);
  const [uploadFor, setUploadFor] = useState(null); // {project, mode}
  // Iter 107 P0A — when the empty-state "Upload plan set" CTA is clicked
  // we chain New Project → Upload Sheets so a first-day customer reaches
  // the publish pipeline in a single flow instead of hunting for the
  // upload button hidden on the project card.
  const [chainUploadAfterCreate, setChainUploadAfterCreate] = useState(false);
  // Iter 83.2B-Polish — set of project_ids the current user watches.
  const [watchedSet, setWatchedSet] = useState(new Set());
  // Founder Commercial Amendment — FORGEDOPS PLANS STARTER (2026-09-XX):
  // archived projects don't consume active_projects/active_pages
  // allowance, but customers still need somewhere to find + reactivate
  // them. Loaded lazily, collapsed by default.
  const [archivedOpen, setArchivedOpen] = useState(false);
  const [archived, setArchived] = useState(null);
  const [restoringId, setRestoringId] = useState(null);
  const { user } = useAuth();
  const showAdmin = canAdminPlans(user);

  const refresh = () => api.get("/projects").then((r) => setProjects(r.data));
  const refreshArchived = () => api.get("/projects", { params: { status: "archived" } }).then((r) => setArchived(r.data));
  useEffect(() => { refresh(); }, []);
  useEffect(() => { if (archivedOpen && archived === null) refreshArchived(); }, [archivedOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const restoreProject = async (projectId) => {
    setRestoringId(projectId);
    try {
      await api.patch(`/projects/${projectId}/restore`);
      window.alert("Project restored — it's back on your active portfolio.");
      refresh();
      refreshArchived();
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Failed to restore project.");
    } finally {
      setRestoringId(null);
    }
  };
  useEffect(() => {
    api.get("/watchlists")
      .then((r) => {
        const next = new Set();
        (r.data || []).forEach((w) => {
          if (w.watch_type === "project" && !w.entity_id) {
            next.add(w.project_id);
          }
        });
        setWatchedSet(next);
      })
      .catch(() => { /* silent */ });
  }, []);

  const filtered = filterPortfolioProjects(projects, q);

  const [sevenDaysAgoIso] = useState(() => new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString());
  const recentRevisionCount = useMemo(() => {
    if (!projects) return 0;
    return projects.filter((p) => p.last_revision_at && p.last_revision_at >= sevenDaysAgoIso).length;
  }, [projects, sevenDaysAgoIso]);

  return (
    <div className="p-8 max-w-[1600px] mx-auto" data-testid="portfolio-page">
      {/* TRACK 3 — Resume card: instant 5:30 AM re-entry into the last
          sheet the operator was viewing. Only renders if there is
          something to resume; never shouts when there isn't. */}
      <div className="mb-6 max-w-md">
        <ResumeCard />
      </div>
      <div className="flex items-end justify-between mb-6 gap-6 flex-wrap">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-[var(--fop-text-3)] mb-2">
            Plan Room
          </div>
          <h1 className="font-display text-4xl text-[var(--fop-text)]">
            Open a project. Jump to the plans.
          </h1>
          <p className="text-[var(--fop-text-2)] mt-2 max-w-2xl">
            Drawings are the source of truth. Open a project to enter the plan set,
            or upload sheets and revisions to keep the field current.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--fop-text-3)]" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search plans, projects, clients…"
              className="pl-9 w-72"
              data-testid="portfolio-search"
            />
          </div>
          {showAdmin && (
            <button
              type="button"
              onClick={() => setNewProjectOpen(true)}
              className="btn btn-primary inline-flex items-center gap-1.5 text-xs whitespace-nowrap"
              data-testid="portfolio-new-project-btn"
              title="Create a new project plan set"
            >
              <Plus size={13} /> New project
            </button>
          )}
        </div>
      </div>

      {recentRevisionCount > 0 && (
        <div
          className="mb-5 flex items-center gap-2.5 px-4 py-2.5 rounded-md border border-[var(--fop-border)] bg-[var(--fop-surface)]"
          data-testid="portfolio-revision-alert"
        >
          <Layers size={14} className="text-[var(--fop-steel)]" />
          <div className="text-xs text-[var(--fop-text)]">
            <span className="font-mono font-bold text-[var(--fop-text)]">{recentRevisionCount}</span>{" "}
            <span className="text-[var(--fop-text-2)]">
              {recentRevisionCount === 1 ? "project has" : "projects have"} new sheet uploads in the last 7 days.
            </span>
          </div>
        </div>
      )}

      {projects === null && (
        <div className="text-[var(--fop-text-3)] font-mono text-xs">Loading plans…</div>
      )}

      {/* Iter 63 P0-2 — first-day empty state. Brand-new tenant lands here
          with zero projects. Instead of a blank screen, surface the
          exact two next actions: create a project, invite the team. */}
      {projects && projects.length === 0 && (
        <EmptyPortfolio
          showAdmin={showAdmin}
          onCreateProject={() => { setChainUploadAfterCreate(false); setNewProjectOpen(true); }}
          onUploadPlanSet={() => { setChainUploadAfterCreate(true); setNewProjectOpen(true); }}
        />
      )}

      {projects && projects.length > 0 && filtered.length === 0 && (
        <div className="fop-surface p-10 text-center text-[var(--fop-text-2)]">
          No projects match your search.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.map((p) => (
          <ProjectCard
            key={p.id}
            project={p}
            showAdmin={showAdmin}
            watching={watchedSet.has(p.id)}
            onHealthClick={() => setHealthFor({ id: p.id, name: p.name })}
            onUpload={(mode) => setUploadFor({ project: p, mode })}
          />
        ))}
      </div>

      {healthFor && (
        <HealthDetailsModal
          projectId={healthFor.id}
          projectName={healthFor.name}
          onClose={() => setHealthFor(null)}
        />
      )}
      {newProjectOpen && (
        <NewProjectModal
          onClose={() => { setNewProjectOpen(false); setChainUploadAfterCreate(false); }}
          onCreated={(created) => {
            refresh();
            if (chainUploadAfterCreate && created) {
              setUploadFor({ project: created, mode: "new" });
            }
            setChainUploadAfterCreate(false);
          }}
        />
      )}
      {uploadFor && (
        <UploadSheetsModal
          projectId={uploadFor.project.id}
          projectName={uploadFor.project.name}
          mode={uploadFor.mode}
          onClose={() => setUploadFor(null)}
          onUploaded={refresh}
        />
      )}

      {showAdmin && projects && projects.length > 0 && (
        <div className="mt-8" data-testid="portfolio-archived-section">
          <button
            type="button"
            onClick={() => setArchivedOpen((v) => !v)}
            className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.18em] text-[var(--fop-text-3)] hover:text-[var(--fop-text)] transition-colors"
            data-testid="portfolio-archived-toggle"
          >
            <Archive size={13} />
            Archived projects
            <ChevronDown size={13} className={`transition-transform ${archivedOpen ? "rotate-180" : ""}`} />
          </button>
          {archivedOpen && (
            <div className="mt-3 space-y-2" data-testid="portfolio-archived-list">
              {archived === null && (
                <div className="text-[var(--fop-text-3)] font-mono text-xs">Loading archived projects…</div>
              )}
              {archived && archived.length === 0 && (
                <div className="text-[var(--fop-text-3)] text-sm">No archived projects.</div>
              )}
              {archived && archived.map((p) => (
                <div
                  key={p.id}
                  className="flex items-center justify-between gap-3 px-4 py-3 rounded-md border border-[var(--fop-border-2)] bg-[var(--fop-surface)]"
                  data-testid={`portfolio-archived-project-${p.number}`}
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-[var(--fop-text)] truncate">{p.name}</div>
                    <div className="text-xs text-[var(--fop-text-3)] font-mono">
                      {p.number} · archived {p.archived_at ? formatRelative(p.archived_at) : ""}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => restoreProject(p.id)}
                    disabled={restoringId === p.id}
                    className="btn btn-ghost text-xs shrink-0 inline-flex items-center gap-1.5"
                    data-testid={`portfolio-restore-btn-${p.number}`}
                  >
                    <RotateCcw size={12} />
                    {restoringId === p.id ? "Restoring…" : "Restore"}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ProjectCard({ project: p, onHealthClick, onUpload, showAdmin, watching }) {
  const recent = p.recent_sheets || [];
  const lastRevisionLabel = p.last_revision_at ? formatRelative(p.last_revision_at) : null;
  const [punch, setPunch] = React.useState(null);
  const [rollCall, setRollCall] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    api.get(`/projects/${p.id}/punch-items/summary`)
      .then((r) => { if (!cancelled) setPunch(r.data); })
      .catch(() => {});
    api.get(`/projects/${p.id}/morning-roll-call`)
      .then((r) => { if (!cancelled) setRollCall(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [p.id]);

  return (
    <div className="fop-card p-6 group relative" data-testid={`project-card-${p.number}`}>
      {/* Header — project identity (Iter 56 field hierarchy: name 24px, number 13px) */}
      <div className="flex items-start gap-3 mb-5">
        <div
          className="w-1.5 self-stretch rounded-full"
          style={{ background: p.cover_color || "var(--fop-primary)" }}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-1">
            <div className="flex items-center gap-2 min-w-0">
              <div className="font-mono text-xs uppercase tracking-wider text-[var(--fop-text-2)] truncate font-semibold">
                {p.number}
              </div>
              {p.internal && <InternalBadge size="xs" />}
              {watching && (
                <span
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full
                              text-[10px] font-mono uppercase tracking-wider font-semibold"
                  style={{
                    background: "color-mix(in oklab, var(--fop-primary) 18%, transparent)",
                    color: "var(--fop-primary)",
                  }}
                  data-testid={`portfolio-watching-${p.number}`}
                  title="You are watching this project"
                >
                  <BellRing size={10} /> Watching
                </span>
              )}
            </div>
          </div>
          <Link to={`/project/${p.id}`} className="block">
            <div className="font-display text-2xl font-semibold text-[var(--fop-text)] leading-tight truncate group-hover:text-[var(--fop-primary)] transition-colors">
              {p.name}
            </div>
            {(p.client || p.location) && (
              <div className="flex items-center gap-2 text-sm text-[var(--fop-text-2)] mt-2">
                {p.client && (
                  <>
                    <Briefcase size={14} />
                    <span className="truncate">{p.client}</span>
                  </>
                )}
                {p.client && p.location && <span className="text-[var(--fop-text-3)]">·</span>}
                {p.location && (
                  <>
                    <MapPin size={14} />
                    <span className="truncate">{p.location}</span>
                  </>
                )}
              </div>
            )}
          </Link>
        </div>
      </div>

      {/* SHEETS-FIRST: recent sheets are the visual hero of the card */}
      <div className="mb-4" data-testid={`project-recent-sheets-${p.number}`}>
        {/* Pillar 5 — War Room badge: only render when there is signal */}
        {rollCall && (rollCall.critical_changes + rollCall.high_changes + rollCall.foremen_pending + rollCall.new_revisions) > 0 && (
          <Link
            to={`/projects/${p.id}/war-room`}
            className="mb-3 flex items-center gap-2 px-3 py-2 rounded border border-[var(--fop-primary)]/45 bg-[var(--fop-primary-soft)] hover:brightness-110 transition-colors"
            data-testid={`project-war-room-badge-${p.number}`}
          >
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--fop-primary)]">War Room</span>
            <span className="text-[11px] text-[var(--fop-text)] truncate flex-1">{rollCall.headline}</span>
            <ChevronRight size={12} className="text-[var(--fop-text-3)] shrink-0" />
          </Link>
        )}
        <div className="flex items-center justify-between mb-2">
          <div className="font-mono text-xs uppercase tracking-[0.18em] text-[var(--fop-text-2)] flex items-center gap-1.5 font-semibold">
            <FileText size={12} /> Recent sheets · {p.kpis?.sheets || 0}
          </div>
          {lastRevisionLabel && (
            <div className="font-mono text-xs uppercase tracking-wider text-[var(--fop-text-3)]">
              {lastRevisionLabel}
            </div>
          )}
        </div>
        {recent.length === 0 ? (
          <Link
            to={`/project/${p.id}`}
            className="block px-4 py-4 rounded-md border border-dashed border-[var(--fop-border-2)] text-sm text-[var(--fop-text-2)] hover:border-[var(--fop-primary)] hover:text-[var(--fop-text)] text-center"
          >
            No sheets uploaded yet. Use Add Sheet to upload the first one.
          </Link>
        ) : (
          <ul className="space-y-1.5">
            {recent.map((s) => (
              <li key={s.id}>
                <Link
                  to={`/sheet/${s.id}`}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-md border border-[var(--fop-border)] bg-[var(--fop-surface)] hover:border-[var(--fop-primary)] hover:bg-[var(--fop-surface-2)] transition-all group/sheet"
                  data-testid={`project-recent-sheet-${s.id}`}
                >
                  <SheetThumb sheetId={s.id} size="sm" data-testid={`project-recent-thumb-${s.id}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="font-mono text-base font-bold text-[var(--fop-text)] group-hover/sheet:text-[var(--fop-primary)] truncate">
                        {s.sheet_number}
                      </div>
                      <div className="font-mono text-xs uppercase tracking-wider text-[var(--fop-text-2)] shrink-0 font-semibold">
                        REV {s.revision}
                      </div>
                    </div>
                    <div className="text-sm text-[var(--fop-text-2)] truncate leading-tight">
                      {s.sheet_title}
                    </div>
                  </div>
                  <ChevronRight size={14} className="text-[var(--fop-text-2)] shrink-0" />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Inline coordination counters — Iter 124 hides the row entirely
          when every counter is zero so brand-new projects don't display
          a meaningless 0/0/0 stat row. */}
      {((p.kpis?.safety || 0) + (p.kpis?.open_pins || 0) + (p.kpis?.open_rfis || 0) > 0 || p.pressure_tag) && (
      <div className="flex items-center gap-4 pt-4 border-t border-[var(--fop-border)] text-sm font-mono uppercase tracking-wider">
        <InlineCounter
          icon={ShieldAlert}
          value={p.kpis?.safety || 0}
          label="Safety"
          color="var(--fop-danger)"
          testid={`project-counter-safety-${p.number}`}
        />
        <InlineCounter
          value={p.kpis?.open_pins || 0}
          label="Open"
          color="var(--fop-warning)"
          testid={`project-counter-open-${p.number}`}
        />
        <InlineCounter
          value={p.kpis?.open_rfis || 0}
          label="RFI"
          color="var(--fop-info)"
          testid={`project-counter-rfi-${p.number}`}
        />
        {p.pressure_tag && (
          <div
            className="ml-auto text-xs font-mono px-2.5 py-1 rounded border border-[rgba(249,115,22,0.25)] bg-[var(--fop-primary-soft)] text-[var(--fop-primary)] truncate max-w-[160px] font-semibold"
            title={p.pressure_tag}
          >
            {p.pressure_tag}
          </div>
        )}
      </div>
      )}

      {/* Disciplines as a quiet footer row */}
      {(p.disciplines || []).length > 0 && (
        <div className="mt-2 flex flex-wrap gap-x-1.5 gap-y-0.5">
          {(p.disciplines || []).slice(0, 5).map((d, i, arr) => (
            <React.Fragment key={d}>
              <span className="text-[9px] uppercase font-mono tracking-wider text-[var(--fop-text-4)]">
                {DISCIPLINE_LABEL[d] || d}
              </span>
              {i < arr.length - 1 && (
                <span className="text-[9px] text-[var(--fop-text-4)]">·</span>
              )}
            </React.Fragment>
          ))}
        </div>
      )}

      {/* Iter 38.5 — Plan-admin quick actions. Visible to authorized roles
          only. Iter 58 — raised tap targets to 44px floor per ARCP directive. */}
      {showAdmin && (
        <div className="mt-3 pt-3 border-t border-[var(--fop-border)] flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); onUpload && onUpload("new"); }}
            className="inline-flex items-center gap-1.5 min-h-[44px] px-3.5 rounded text-xs font-mono uppercase tracking-wider border border-[var(--fop-border-2)] text-[var(--fop-text)] hover:border-[var(--fop-primary)] hover:text-[var(--fop-primary)] transition-colors"
            data-testid={`project-add-sheet-${p.number}`}
          >
            <Plus size={14} /> Add sheet
          </button>
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); onUpload && onUpload("revision"); }}
            className="inline-flex items-center gap-1.5 min-h-[44px] px-3.5 rounded text-xs font-mono uppercase tracking-wider border border-[var(--fop-border-2)] text-[var(--fop-text)] hover:border-[var(--fop-primary)] hover:text-[var(--fop-primary)] transition-colors"
            data-testid={`project-upload-revision-${p.number}`}
          >
            <Upload size={14} /> Upload revision
          </button>
          <Link
            to={`/project/${p.id}`}
            className="inline-flex items-center gap-1.5 min-h-[44px] px-3.5 rounded text-xs font-mono uppercase tracking-wider border border-[var(--fop-border-2)] text-[var(--fop-text)] hover:border-[var(--fop-steel)] hover:text-[var(--fop-text)] transition-colors ml-auto"
            data-testid={`project-manage-${p.number}`}
            title="Manage plan set"
          >
            <Settings2 size={14} /> Manage
          </Link>
        </div>
      )}

      {/* Iter 82.3 — Portfolio Punch KPI strip. Reads from existing
          summary endpoint, no duplicate aggregation. Hides when empty. */}
      {punch && punch.total > 0 && (
        <div className="mt-3 pt-3 border-t border-[var(--fop-border)] flex items-center gap-3 text-[11px]" data-testid={`punch-kpi-${p.number}`}>
          <span className="font-mono uppercase tracking-wider text-[var(--fop-text-2)]">Punch</span>
          <span className="inline-flex items-center gap-1 text-[var(--fop-primary)]">
            <span className="h-1.5 w-1.5 rounded-full bg-[var(--fop-primary)]" />
            <span className="font-semibold">{punch.open}</span> open
          </span>
          <span className="inline-flex items-center gap-1 text-[#facc15]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#facc15]" />
            <span className="font-semibold">{punch.ready_for_review}</span> review
          </span>
          <span className="inline-flex items-center gap-1 text-[#22c55e]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#22c55e]" />
            <span className="font-semibold">{punch.closed}</span> closed
          </span>
          <span className="ml-auto font-mono text-[var(--fop-text-2)]">
            {Math.round(punch.completion_pct || 0)}% complete
          </span>
        </div>
      )}
    </div>
  );
}

function InlineCounter({ icon: Icon, value, label, color, testid }) {
  return (
    <div className="flex items-center gap-1.5" data-testid={testid}>
      {Icon && <Icon size={10} style={{ color }} />}
      <span className="font-bold text-sm" style={{ color }}>{value}</span>
      <span className="text-[var(--fop-text-3)]">{label}</span>
    </div>
  );
}

/**
 * Iter 63 P0-2 — First-day empty state.
 *
 * A brand-new tenant lands here with zero projects. The fix is not "show
 * a friendlier blank" — it's "tell them the next action and let them do
 * it in one tap". Two CTAs, no marketing copy.
 */
function EmptyPortfolio({ showAdmin, onCreateProject, onUploadPlanSet }) {
  return (
    <div
      className="fop-surface rounded-lg border border-[var(--fop-border)] p-8 md:p-12 max-w-3xl mx-auto"
      data-testid="portfolio-empty-state"
    >
      <div className="text-center mb-7">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--fop-text-3)] mb-2">
          Plan room
        </div>
        <h2 className="font-display text-3xl text-[var(--fop-text)] leading-tight">
          Get your team into the plans.
        </h2>
      </div>

      {/* 4-step checklist — field language, no software words */}
      <ol className="space-y-3 mb-7" data-testid="empty-checklist">
        <Step n="1" title="Create a project" body="One project per job. It holds every sheet and every revision." done={false}
          cta={showAdmin ? (
            <button type="button" onClick={onCreateProject} data-testid="portfolio-empty-create-project"
              className="btn btn-primary inline-flex items-center gap-2 min-h-[44px] px-5 text-sm">
              <Plus size={16} /> Create project
            </button>
          ) : null} />
        <Step n="2" title="Upload the plan set" body="Drop in PDFs. Every page becomes a sheet you can open from the field." done={false}
          cta={showAdmin ? (
            <button type="button" onClick={onUploadPlanSet} data-testid="portfolio-empty-upload-plan-set"
              className="btn btn-primary inline-flex items-center gap-2 min-h-[44px] px-5 text-sm">
              <Upload size={16} /> Upload plan set
            </button>
          ) : null} />
        <Step n="3" title="Invite the team" body="Foremen, superintendents, inspectors — they sign in, they see the same plans." done={false}
          cta={showAdmin ? (
            <Link to="/admin/users" data-testid="portfolio-empty-invite-team"
              className="inline-flex items-center gap-2 min-h-[44px] px-4 rounded-md border border-[var(--fop-border-2)] text-sm text-[var(--fop-text)] hover:border-[var(--fop-primary)] hover:text-[var(--fop-primary)] transition-colors">
              <UsersIcon size={16} /> Invite team
            </Link>
          ) : null} />
        <Step n="4" title="Coordinate work" body="Punch items, revisions, photos — every decision in one place." done={false} />
      </ol>

      {!showAdmin && (
        <div className="text-sm text-[var(--fop-text-2)] border border-dashed border-[var(--fop-border-2)] rounded-md px-4 py-3"
          data-testid="portfolio-empty-viewer-notice">
          Your project administrator will set up the first project. Once plans are uploaded, they will show up here.
        </div>
      )}
    </div>
  );
}

function Step({ n, title, body, done, cta }) {
  return (
    <li className="flex items-start gap-3" data-testid={`empty-step-${n}`}>
      <div className={`shrink-0 w-7 h-7 rounded-full inline-flex items-center justify-center font-mono text-[11px] font-bold border ${done ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300" : "bg-[var(--fop-surface)] border-[var(--fop-border-2)] text-[var(--fop-text-3)]"}`}>
        {done ? <Check size={14} /> : n}
      </div>
      <div className="flex-1 pt-0.5">
        <div className="text-[var(--fop-text)] font-medium">Step {n} — {title}</div>
        <div className="text-[13px] text-[var(--fop-text-3)] mt-0.5">{body}</div>
        {cta && <div className="mt-2">{cta}</div>}
      </div>
    </li>
  );
}
